import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

/**
 * Individual stop markers with clear selection feedback.
 * - Unselected: small blue canvas circle (fast for 1000+)
 * - Selected: larger green marker WITH SELECTION NUMBER (order-of-selection)
 * - Left-click: toggle selection
 * - Right-click: popup with delete
 * Props:
 *   markers: [{ id, lat, lng, title, address, selected?, order? }]  // order = 1-based selection index
 *   onToggleSelect: (marker) => void
 *   onDelete: (marker) => void
 */
export default function StopsLayer({ markers = [], onToggleSelect, onDelete }) {
  const map = useMap();
  const groupRef = useRef(null);
  const handlersRef = useRef({ onToggleSelect, onDelete });
  handlersRef.current = { onToggleSelect, onDelete };

  useEffect(() => {
    const group = L.layerGroup();
    groupRef.current = group;
    map.addLayer(group);
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.clearLayers();

    const escape = (s) => String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

    // Two passes: unselected first (background), selected on top with numbered icons
    const selected = [];
    for (const m of markers) {
      if (m.lat == null || m.lng == null) continue;
      if (m.selected) {
        selected.push(m);
        continue;
      }
      const marker = L.circleMarker([m.lat, m.lng], {
        radius: 7,
        color: "#ffffff",
        weight: 2,
        fillColor: "#2563eb",
        fillOpacity: 0.95,
        interactive: true,
        bubblingMouseEvents: false,
      });
      attachHandlers(marker, m);
      group.addLayer(marker);
    }

    // Selected markers on top, with numbered divIcon
    for (const m of selected) {
      const num = m.order || "✓";
      const html = `<div style="
          width:30px;height:30px;border-radius:50%;
          background:#10b981;color:white;
          border:3px solid #fff;
          box-shadow:0 4px 12px rgba(16,185,129,0.6);
          display:flex;align-items:center;justify-content:center;
          font-family:Outfit,sans-serif;font-weight:800;font-size:13px;
          transition:transform .15s;">${num}</div>`;
      const icon = L.divIcon({
        html, className: "ff-selected-marker",
        iconSize: [30, 30], iconAnchor: [15, 15],
      });
      const marker = L.marker([m.lat, m.lng], { icon, zIndexOffset: 1000 });
      attachHandlers(marker, m);
      group.addLayer(marker);
    }

    function attachHandlers(marker, m) {
      marker.on("contextmenu", (e) => {
        L.DomEvent.stop(e);
        const container = L.DomUtil.create("div");
        container.style.fontFamily = "'Manrope', sans-serif";
        container.style.minWidth = "220px";
        container.innerHTML = `
          <div style="font-weight:700;font-size:14px;margin-bottom:2px">${escape(m.title)}</div>
          <div style="font-size:12px;color:#64748b;margin-bottom:10px">${escape(m.address)}</div>
          <button data-act="delete" style="width:100%;background:#ef4444;color:white;border:0;border-radius:999px;padding:9px 12px;font-size:13px;font-weight:700;cursor:pointer">Adresse löschen</button>`;
        const btn = container.querySelector('[data-act="delete"]');
        L.DomEvent.on(btn, "click", (ev) => {
          L.DomEvent.stop(ev);
          handlersRef.current.onDelete?.(m);
          marker.closePopup();
        });
        marker.bindPopup(container, { closeButton: true }).openPopup();
      });

      marker.on("click", (e) => {
        L.DomEvent.stop(e);
        handlersRef.current.onToggleSelect?.(m);
      });

      marker.bindTooltip(
        m.selected
          ? `<b>${m.order ? "#" + m.order + " · " : ""}${escape(m.title || "Stopp")}</b><br><span style="opacity:.7">Klick zum Entfernen</span>`
          : `${escape(m.title || "Stopp")}<br><span style="opacity:.7">Klick zum Markieren</span>`,
        { direction: "top", offset: [0, -6], opacity: 0.95, className: "stop-tooltip" }
      );
    }
  }, [markers]);

  return null;
}
